/**
 * AuditBadge — minimal verify status pill on the operator session sidebar.
 *
 * Calls GET /sessions/{id}/audit/verify and renders one of:
 *   - ✓ Verified · N events · last anchor in block #M (when fully ok)
 *   - ⏳ Pending · N events · K token(s) inflight (chain ok, anchor not yet)
 *   - ✗ Tampered · break at sequence S (chain failed)
 *   - — (when the session predates the audit chain or the user lacks the
 *      `audit.verify` permission — silent rather than confusing)
 *
 * Polls every 60s while open so a pending anchor flips to verified once
 * Tezos confirms (typically ~30s after stamp). React Query pauses while
 * the tab is hidden.
 *
 * Click → opens a small popover with the per-token list. Phase 2 will
 * promote this to a richer auditor dashboard.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ApiPaths, api } from "@/lib/api";


type TokenResult = {
  token_id: string;
  /** Which TSA backend produced this token. New in the multi-backend
   *  refactor — tells the operator UI which icon + label to render. */
  backend: "tezos_yodaledger" | "rfc3161_freetsa" | "rfc3161_datasure" | "rfc3161_generic";
  state: "pending" | "anchored" | "failed";
  ok: boolean;
  covers_sequence_through: number;
  chain_head_hash: string;
  /** Backend-agnostic anchor coordinates. For Tezos these are block /
   *  tx hashes; for RFC 3161 the serial + TSA URL are the meaningful ones. */
  anchor_tx_hash?: string;
  anchor_block_hash?: string;
  anchor_block_depth?: number;
  anchor_serial?: string;
  anchor_url?: string;
  blockchain?: string;
  anchored_at?: string | null;
  submitted_at?: string | null;
};

type VerifyResponse = {
  ok: boolean;
  reason?: string;
  events: number;
  tokens: number;
  broken: null | {
    sequence: number;
    expected_hash: string;
    stored_hash: string;
  };
  token_results: TokenResult[];
  chain_head_hash: string | null;
  audit_chain_started_at: string | null;
};


type Props = { sessionId: string };


export function AuditBadge({ sessionId }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ["audit-verify", sessionId],
    queryFn: async () => {
      const res = await api.get<VerifyResponse>(
        `/v1/sessions/${sessionId}/audit/verify`,
      );
      return res.data;
    },
    refetchInterval: 60_000,
    // 403 means the user doesn't have audit.verify — silently hide
    // the badge by returning a sentinel from the query function.
    retry: false,
  });

  if (q.isLoading) {
    return <span className="text-[10px] text-nb-text-3">{t("ui.auditBadge.loading")}</span>;
  }
  if (q.isError) {
    // Most likely 403 (perm) — silent rather than misleading.
    return null;
  }
  const data = q.data;
  if (!data) return null;

  // Pre-1B-D session — quietly skip.
  if (data.reason?.includes("predates")) {
    return null;
  }

  const pending = data.token_results.filter((t) => t.state === "pending").length;
  const anchored = data.token_results.filter((t) => t.state === "anchored");
  // Count distinct backends covering the latest head so the operator
  // sees "Tezos + RFC 3161" coverage at a glance.
  const anchoredBackends = new Set(anchored.map((t) => t.backend));

  let label: string;
  let className: string;
  if (data.broken) {
    label = t("ui.auditBadge.tamperedLabel", { sequence: data.broken.sequence });
    className = "bg-cvp-danger/20 text-cvp-danger";
  } else if (data.ok && anchored.length > 0) {
    const backendCount = anchoredBackends.size;
    label =
      backendCount > 1
        ? t("ui.auditBadge.verifiedMultiLabel", {
            events: data.events,
            count: backendCount,
          })
        : t("ui.auditBadge.verifiedLabel", { events: data.events });
    className = "bg-cvp-ok/20 text-cvp-ok";
  } else if (pending > 0) {
    label = t("ui.auditBadge.anchoringLabel", {
      events: data.events,
      pending,
    });
    className = "bg-cvp-warn/20 text-cvp-warn";
  } else {
    label = t("ui.auditBadge.eventsLabel", { events: data.events });
    className = "bg-nb-surface/40 text-nb-text-2";
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium transition hover:opacity-80 ${className}`}
        aria-label={t("ui.auditBadge.statusAria", { label })}
      >
        {label}
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-1 w-72 rounded-md border border-nb-border bg-nb-bg/95 p-3 text-xs shadow-xl">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="font-semibold text-nb-text">{t("ui.auditBadge.popoverTitle")}</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-nb-text-3 hover:text-nb-text"
              aria-label={t("ui.auditBadge.closeAria")}
            >
              ×
            </button>
          </div>
          <dl className="space-y-1 text-nb-text">
            <Row label={t("ui.auditBadge.rowEvents")} value={String(data.events)} />
            <Row
              label={t("ui.auditBadge.rowTokens")}
              value={t("ui.auditBadge.tokensValue", {
                anchored: anchored.length,
                pending,
              })}
            />
            {data.chain_head_hash && (
              <Row
                label={t("ui.auditBadge.rowHead")}
                value={<code className="font-mono">{data.chain_head_hash.slice(0, 12)}…</code>}
              />
            )}
          </dl>
          {/* Per-backend block: shows each TSA's status so the operator
              can see "Tezos pending, RFC 3161 anchored" at a glance. */}
          {data.token_results.length > 0 && (
            <ul className="mt-2 space-y-1.5 border-t border-nb-border pt-2 text-[11px]">
              {data.token_results.map((t) => (
                <li key={t.token_id} className="flex items-baseline justify-between gap-2">
                  <span className="text-nb-text-2">{backendLabel(t.backend)}</span>
                  <BackendStatus token={t} />
                </li>
              ))}
            </ul>
          )}
          {data.broken && (
            <p className="mt-2 text-[10px] text-cvp-danger">
              {t("ui.auditBadge.brokenNote", { sequence: data.broken.sequence })}
            </p>
          )}
          {anchored.length === 0 && pending > 0 && (
            <p className="mt-2 text-[10px] text-nb-text-3">
              {t("ui.auditBadge.anchoringNote")}
            </p>
          )}
          <div className="mt-2 border-t border-nb-border pt-2">
            <Link
              to={`/sessions/${sessionId}/audit`}
              className="text-[11px] text-nb-mint hover:underline"
              onClick={() => setOpen(false)}
            >
              {t("ui.auditBadge.viewDetails")}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}


function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-nb-text-3">{label}</dt>
      <dd className="text-right text-nb-text">{value}</dd>
    </div>
  );
}


function backendLabel(backend: TokenResult["backend"]): string {
  switch (backend) {
    case "tezos_yodaledger":
      // `tezos_yodaledger` is the backend_id stamped on historical
      // TimestampToken rows (code identifier). The customer-facing
      // label leads with "Tezos blockchain" — YodaLedger is our
      // relay onto Tezos, not what users care about.
      return "Tezos blockchain";
    case "rfc3161_freetsa":
      return "RFC 3161 (FreeTSA)";
    case "rfc3161_datasure":
      return "RFC 3161 (DataSure)";
    case "rfc3161_generic":
      return "RFC 3161";
    default:
      return backend;
  }
}


function BackendStatus({ token }: { token: TokenResult }) {
  const { t } = useTranslation();
  if (token.state === "pending") {
    return <span className="text-cvp-warn">{t("ui.auditBadge.statusPending")}</span>;
  }
  if (token.state === "failed") {
    return <span className="text-cvp-danger">{t("ui.auditBadge.statusFailed")}</span>;
  }
  // Anchored — show the most-useful per-backend coordinate inline.
  const detail =
    token.backend === "tezos_yodaledger" && token.anchor_block_depth
      ? t("ui.auditBadge.statusDepth", { depth: token.anchor_block_depth })
      : token.anchor_serial
      ? t("ui.auditBadge.statusSerial", { serial: token.anchor_serial })
      : t("ui.auditBadge.statusAnchored");
  return <span className="text-cvp-ok">{detail}</span>;
}


// silence unused-import warning for ApiPaths — leaving the import in the
// barrel section above so future endpoints can reach for it without re-
// adding the import.
void ApiPaths;
